import _defineProperty from "/Users/lucas/fall-2019/capstone/continuum-app/client/node_modules/@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { sortedUniqNoteNames, rotate } from '@tonaljs/array';
import { entries } from '@tonaljs/chord-dictionary';
import { isSubsetOf, isSupersetOf, modes } from '@tonaljs/pcset';
import { scaleType, entries as entries$1 } from '@tonaljs/scale-dictionary';
import { note, transpose } from '@tonaljs/tonal';
var NoScale = {
  empty: true,
  name: "",
  type: "",
  tonic: null,
  setNum: NaN,
  chroma: "",
  normalized: "",
  aliases: [],
  notes: [],
  intervals: []
};
/**
 * Given a string with a scale name and (optionally) a tonic, split
 * that components.
 *
 * It retuns an array with the form [ name, tonic ] where tonic can be a
 * note name or null and name can be any arbitrary string
 * (this function doesn"t check if that scale name exists)
 *
 * @function
 * @param {string} name - the scale name
 * @return {Array} an array [tonic, name]
 * @example
 * tokenize("C mixolydean") // => ["C", "mixolydean"]
 * tokenize("anything is valid") // => ["", "anything is valid"]
 * tokenize() // => ["", ""]
 */

function tokenize(name) {
  if (typeof name !== "string") {
    return ["", ""];
  }

  var i = name.indexOf(" ");
  var tonic = note(name.substring(0, i));

  if (tonic.empty) {
    var n = note(name);
    return n.empty ? ["", name] : [n.name, ""];
  }

  var type = name.substring(tonic.name.length + 1);
  return [tonic.name, type.length ? type : ""];
}
/**
 * Get a Scale from a scale name.
 */


function scale(src) {
  var tokens = Array.isArray(src) ? src : tokenize(src);
  var tonic = note(tokens[0]).name;
  var st = scaleType(tokens[1]);

  if (st.empty) {
    return NoScale;
  }

  var type = st.name;
  var notes = tonic ? st.intervals.map(function (i) {
    return transpose(tonic, i);
  }) : [];
  var name = tonic ? tonic + " " + type : type;
  return _objectSpread({}, st, {
    name: name,
    type: type,
    tonic: tonic,
    notes: notes
  });
}
/**
 * Get all chords that fits a given scale
 *
 * @function
 * @param {string} name - the scale name
 * @return {Array<string>} - the chord names
 *
 * @example
 * scaleChords("pentatonic") // => ["5", "64", "M", "M6", "Madd9", "Msus2"]
 */


function scaleChords(name) {
  var s = scale(name);
  var inScale = isSubsetOf(s.chroma);
  return entries().filter(function (chord) {
    return inScale(chord.chroma);
  }).map(function (chord) {
    return chord.aliases[0];
  });
}
/**
 * Get all scales names that are a superset of the given one
 * (has the same notes and at least one more)
 *
 * @function
 * @param {string} name
 * @return {Array} a list of scale names
 * @example
 * extended("major") // => ["bebop", "bebop dominant", "bebop major", "chromatic", "ichikosucho"]
 */


function extended(name) {
  var s = scale(name);
  var isSuperset = isSupersetOf(s.chroma);
  return entries$1().filter(function (scale) {
    return isSuperset(scale.chroma);
  }).map(function (scale) {
    return scale.name;
  });
}
/**
 * Find all scales names that are a subset of the given one
 * (has less notes but all from the given scale)
 *
 * @function
 * @param {string} name
 * @return {Array} a list of scale names
 *
 * @example
 * reduced("major") // => ["ionian pentatonic", "major pentatonic", "ritusen"]
 */


function reduced(name) {
  var isSubset = isSubsetOf(scale(name).chroma);
  return entries$1().filter(function (scale) {
    return isSubset(scale.chroma);
  }).map(function (scale) {
    return scale.name;
  });
}
/**
 * Given an array of notes, return the scale: a pitch class set starting from
 * the first note of the array
 *
 * @function
 * @param {string[]} notes
 * @return {string[]} pitch classes with same tonic
 * @example
 * scaleNotes(['C4', 'c3', 'C5', 'C4', 'c4']) // => ["C"]
 * scaleNotes(['D4', 'c#5', 'A5', 'F#6']) // => ["D", "F#", "A", "C#"]
 */


function scaleNotes(notes) {
  var pcset = notes.map(function (n) {
    return note(n).pc;
  }).filter(function (x) {
    return x;
  });
  var tonic = pcset[0];
  var scale = sortedUniqNoteNames(pcset);
  return rotate(scale.indexOf(tonic), scale);
}
/**
 * Find mode names of a scale
 *
 * @function
 * @param {string} name - scale name
 * @example
 * modeNames("C pentatonic") // => [
 *   ["C", "major pentatonic"],
 *   ["D", "egyptian"],
 *   ["E", "malkos raga"],
 *   ["G", "ritusen"],
 *   ["A", "minor pentatonic"]
 * ]
 */


function modeNames(name) {
  var s = scale(name);

  if (s.empty) {
    return [];
  }

  var tonics = s.tonic ? s.notes : s.intervals;
  return modes(s.chroma).map(function (chroma, i) {
    var modeName = scale(chroma).name;
    return modeName ? [tonics[i], modeName] : ["", ""];
  }).filter(function (x) {
    return x[0];
  });
}

export { extended, modeNames, reduced, scale, scaleChords, scaleNotes, tokenize };